import {
  getBlogPosts as dbGetBlogPosts,
  addBlogPost as dbAddBlogPost,
  updateBlogPost as dbUpdateBlogPost,
  deleteBlogPost as dbDeleteBlogPost,
  getBlogPostBySlug as dbGetBlogPostBySlug,
  initDB,
} from "./db";
import { normalizeBlogPost } from "./blogContent";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const BLOG_POSTS_TABLE = "blog_posts";

const mapBlogPostPayload = (postData) => ({
  title: postData.title,
  slug: postData.slug,
  excerpt: postData.excerpt,
  content: postData.content,
  content_format: postData.contentFormat || "html",
  author: postData.author,
  role: postData.role,
  category: postData.category,
  image: postData.image,
  read_time: postData.readTime,
  tags: postData.tags || [],
  schema_version: postData.schemaVersion || 2,
});

const normalizeSupabasePost = (row) =>
  normalizeBlogPost({
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    content: row.content,
    contentFormat: row.content_format || "html",
    author: row.author,
    role: row.role,
    category: row.category,
    image: row.image,
    readTime: row.read_time,
    tags: Array.isArray(row.tags) ? row.tags : [],
    date: row.published_at || row.created_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    schemaVersion: row.schema_version || 2,
  });

export const getBlogPosts = async () => {
  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from(BLOG_POSTS_TABLE)
      .select("*")
      .order("published_at", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching blog posts from Supabase:", error);
      return [];
    }

    return data.map(normalizeSupabasePost);
  }

  await initDB();
  return dbGetBlogPosts();
};

export const addBlogPost = async (postData) => {
  if (isSupabaseConfigured) {
    const normalizedPost = normalizeBlogPost(postData);
    const { data, error } = await supabase
      .from(BLOG_POSTS_TABLE)
      .insert({
        ...mapBlogPostPayload(normalizedPost),
        published_at: new Date().toISOString(),
      })
      .select("*")
      .single();

    if (error) {
      console.error("Error adding blog post to Supabase:", error);
      throw error;
    }

    return normalizeSupabasePost(data);
  }

  await initDB();
  return dbAddBlogPost(postData);
};

export const updateBlogPost = async (id, postData) => {
  if (isSupabaseConfigured) {
    const normalizedPost = normalizeBlogPost(postData);
    const { data, error } = await supabase
      .from(BLOG_POSTS_TABLE)
      .update(mapBlogPostPayload(normalizedPost))
      .eq("id", id)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating blog post in Supabase:", error);
      throw error;
    }

    return normalizeSupabasePost(data);
  }

  await initDB();
  return dbUpdateBlogPost(id, postData);
};

export const deleteBlogPost = async (id) => {
  if (isSupabaseConfigured) {
    const { error } = await supabase.from(BLOG_POSTS_TABLE).delete().eq("id", id);

    if (error) {
      console.error("Error deleting blog post from Supabase:", error);
      throw error;
    }

    return true;
  }

  await initDB();
  return dbDeleteBlogPost(id);
};

export const getBlogPostBySlug = async (slug) => {
  if (isSupabaseConfigured) {
    const { data, error } = await supabase
      .from(BLOG_POSTS_TABLE)
      .select("*")
      .eq("slug", slug)
      .single();

    if (error) {
      console.error("Error fetching blog post by slug from Supabase:", error);
      return null;
    }

    return normalizeSupabasePost(data);
  }

  await initDB();
  return dbGetBlogPostBySlug(slug);
};

export const resetBlogPosts = async () => {
  await initDB();
  const { clearAllData } = await import("./db");
  await clearAllData();
};
